import { Button, Grid2, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOrganizationState } from 'features/organizations/state/useOrganizationState';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { useHistory } from 'react-router-dom';
import { useOrganizationPurchase } from 'features/organizations/hooks/useOrganizationPurchase';
import { enqueueSnackbar } from 'notistack';
import { OrganizationPurchaseSummary } from '../OrganizationPurchaseSummary';

interface OrganizationQuoteData {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  additionalEmails: string;
  postalCode: string;
  poNumber?: string;
  regularSeats: number;
  proSeats: number;
}

const getValidationSchema = (t: TFunction): Yup.AnyObjectSchema => {
  return Yup.object().shape({
    companyName: Yup.string().required(
      t('organizationPurchase.companyName.required'),
    ),
    firstName: Yup.string().required(
      t('organizationPurchase.firstName.required'),
    ),
    lastName: Yup.string().required(
      t('organizationPurchase.lastName.required'),
    ),
    address: Yup.string().required(t('organizationPurchase.address.required')),
    postalCode: Yup.string().required(
      t('organizationPurchase.zipcode.required'),
    ),
    email: Yup.string()
      .email(t('organizationPurchase.email.invalid'))
      .required(t('organizationPurchase.email.required')),
    proSeats: Yup.number()
      .typeError(t('organizationPurchase.proSeats.required'))
      .min(0, t('organizationPurchase.proSeats.min'))
      .required(t('organizationPurchase.proSeats.required')),
    regularSeats: Yup.number()
      .typeError(t('organizationPurchase.regularSeats.required'))
      .min(0, t('organizationPurchase.regularSeats.min'))
      .required(t('organizationPurchase.regularSeats.required')),
    poNumber: Yup.string().required(
      t('organizationPurchase.poNumber.required'),
    ),
  });
};

export const OrganizationQuoteInfoForm = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const { sendOrganizationQuote } = useOrganizationPurchase({});
  const { regularSeats, proSeats, ...orgState } = useOrganizationState();

  const { control, handleSubmit, formState } = useForm<OrganizationQuoteData>({
    resolver: yupResolver(getValidationSchema(t)),
    defaultValues: {
      companyName: orgState.companyName || '',
      firstName: orgState.firstName || '',
      lastName: orgState.lastName || '',
      email: orgState.email || '',
      address: '',
      additionalEmails: '',
      postalCode: '',
      poNumber: '',
      regularSeats: regularSeats || 0,
      proSeats: proSeats || 0,
    },
  });

  const { errors } = formState;

  const handleFormSubmit = async (
    values: OrganizationQuoteData,
  ): Promise<void> => {
    try {
      await sendOrganizationQuote({
        ...values,
        regularSeats,
        proSeats,
      });

      enqueueSnackbar(t('organizationPurchase.quoteSuccess'), {
        variant: 'success',
      });
      history.push('/');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const errorKey = err?.response?.data?.[0];
      if (errorKey === 'organization-already-exists') {
        enqueueSnackbar(t('organizationPurchase.quoteOrgExists'), {
          variant: 'error',
          persist: true,
        });
      } else {
        enqueueSnackbar(t('common.unknownErrorTryAgain'), { variant: 'error' });
      }
    }
  };

  return (
    <Grid2
      container
      direction="column"
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Grid2 container direction="column" spacing={2}>
        <Grid2>
          <Typography variant="h3">
            <b>{t('organizationPurchase.quoteInfo')}</b>
          </Typography>
        </Grid2>

        <Grid2>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.companyName.label')}
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
                required
                {...field}
              />
            )}
          />
        </Grid2>

        <Grid2 container spacing={2} direction="row">
          <Grid2 size={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.firstName.label')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.lastName.label')}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid2>
        </Grid2>

        <Grid2>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.email.label')}
                error={!!errors.email}
                helperText={errors.email?.message}
                required
                {...field}
              />
            )}
          />
        </Grid2>

        <Grid2>
          <Controller
            name="additionalEmails"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.additionalEmails.label')}
                helperText={t(
                  'organizationPurchase.additionalEmails.helperText',
                )}
                {...field}
              />
            )}
          />
        </Grid2>

        <Grid2>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.address.label')}
                error={!!errors.address}
                helperText={errors.address?.message}
                required
                {...field}
              />
            )}
          />
        </Grid2>

        <Grid2 container spacing={2} direction="row">
          <Grid2 size={6}>
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.zipcode.label')}
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid2>
        </Grid2>
        <Grid2>
          <Controller
            name="poNumber"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.poNumber.label')}
                error={!!errors.poNumber}
                helperText={errors.poNumber?.message}
                required
                {...field}
              />
            )}
          />
        </Grid2>

        <Grid2>
          <Typography variant="h2">{t('organization.featureName')}</Typography>
        </Grid2>

        <Grid2 container justifyContent="space-between">
          <Typography component="span">
            {t('organizationPurchase.membership')}
          </Typography>
          <Typography component="span">{regularSeats}</Typography>
        </Grid2>
        <Grid2 container justifyContent="space-between">
          <Typography component="span">
            {t('organizationPurchase.pro')}
          </Typography>
          <Typography component="span">{proSeats}</Typography>
        </Grid2>

        <OrganizationPurchaseSummary
          regularSeats={regularSeats}
          proSeats={proSeats}
        />
      </Grid2>

      <Grid2
        size="grow"
        container
        direction="column"
        sx={{ justifyContent: 'flex-end' }}
      >
        <Button
          size="large"
          fullWidth
          type="submit"
          disabled={formState.isSubmitting}
        >
          {t('organizationPurchase.sendAndEmailQuote')}
        </Button>
      </Grid2>
    </Grid2>
  );
};
